/*
* Created on Mar 10, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import { useState, useMemo, useRef, useEffect } from 'react';
import ModalComponent from "../common/ModalComonent/ModalComponent";
import useToggle from "../../hook/ToggleHook";
import {useQuery} from "@tanstack/react-query";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useTimezone} from "../../service/timezone";
import {useOrganization} from "../../context/OrganizationContext";
import {useRegion} from "../../context/RegionContext";


const AccountListEditAdminModalComponent = ({openModal, closeModal, user_data, doRefresh, organization_id}) => {
    const {t} = useTranslation();
    const {OrganizationAPI} = useOrganizationApi();
    const {oid} = useParams()
    const [user, setUser] = useState(user_data);
    const [sites, setSites] = useState([]);
    const last_update = useTimezone(user?.status_updated_at, false);
    const {userProfile} = useOrganization();
    const {is_owner, permissions} = userProfile();
    const modal = useRef();
    const {url} = useRegion();

    const {} = useQuery({
        queryKey: [`organization/${oid}/user/${user.id}/site`],
        queryFn: async () => {
            const group_list = await OrganizationAPI.get(`organization/${oid}/user/${user.id}/site`);
            setSites(group_list.result);
            return group_list.result;
        }
    });

    const isOwner = useMemo(()=>{
        const owner = user.hasOwnProperty('is_owner') && user.is_owner === true;
        return owner;
    }, [user_data.is_owner, user.is_owner]);

    const canInvite = useMemo(()=>{
        return (user.hasOwnProperty('permissions') && user.permissions.hasOwnProperty('invite_admin')) ||
            (user.hasOwnProperty('__invite__') && user.__invite__.permission === 'invite_admin');
    }, [user_data.permissions, user.permissions, user?.__invite__?.permission]);

    const prevDescription = useRef(user.description);

    const onSubmit = () => {}

    const removeGroup = async (group) => {
        try {
            await OrganizationAPI.delete(`organization/${organization_id}/group/${group.id}/user`,
            null, {emails:[user.email || user.qid]});
            setUser(prevU=>{
                const newG = prevU.groups.filter((g)=>g.id !== group.id)
                return {
                    ...prevU, groups: newG
                }
            });
            doRefresh(prevRefresh=>prevRefresh+1);
            modal.current.alert(true, 'success', 0, t("@Your profile is updated.@"));
        } catch (err) {
            console.log(err);
            modal.current.alert(true, 'error', err.response.data.code, err.response.data.message);
            throw err;
        }

    }

    const removeAdmin = async (site_id) => {
        const data = {
            'admin': {
                'users': [{
                    'id': user.id,
                    'email': user.email
                }]
            }
        }
        try {
            await OrganizationAPI.delete(`organization/${oid}/site/${site_id}/admin`, null, data);
            setSites(prevS=>{
                const newS = prevS.filter((s)=>s.organization_site_id !== site_id)
                return [...newS]
            });
            modal.current.alert(true, 'success', 0, t("@Your profile is updated.@"));
        } catch (e) {
            modal.current.alert(true, 'error', e.response.data.code, e.response.data.message);
            throw e;
        }
    }

    const toggleOwner = async () => {
        try {
            await OrganizationAPI.put(`organization/${organization_id}/qid/${user.email || user.qid}`, null, {is_owner: !user.is_owner })
            setUser((prevUser)=> {
                const owner = prevUser.hasOwnProperty('is_owner') && user.is_owner === true;
                return {...prevUser, is_owner: !owner}
            });
            doRefresh(prevRefresh=>prevRefresh+1);
            modal.current.alert(true, 'success', 0, t("@Your profile is updated.@"));
        } catch (err) {
            modal.current.alert(true, 'error', err.response.data.code, err.response.data.message);
        }

    }

    const toggleInviteAdmin = async () => {
        try {
            const resp = await OrganizationAPI.put(`organization/${organization_id}/user/${user.id}/enableInvite`, null, {enable: !canInvite});
            setUser((prevUser)=> {
                return {...prevUser, permissions: resp.result}
            });
            doRefresh(prevRefresh=>prevRefresh+1);
            modal.current.alert(true, 'success', 0, t("@Your profile is updated.@"));
        } catch (err) {
            modal.current.alert(true, 'error', err.response.data.code, err.response.data.message);
            throw err;
        }
    }

    const updateDescription = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (prevDescription.current !== user.description) {
                try {
                    await OrganizationAPI.put(`organization/${organization_id}/qid/${user.email || user.qid}`, null, {description: user.description})
                    prevDescription.current = user.description;
                    doRefresh(prevRefresh=>prevRefresh+1);
                    modal.current.alert(true, 'success', 0, t("@Your profile is updated.@"));
                } catch (err) {
                    console.log(err);
                    modal.current.alert(true, 'error', err.response.data.code, err.response.data.message);
                }
            }
        }

    }

    const selectStatus = async (status) => {
        if (status !== user.status) {
            try {
                await OrganizationAPI.put(`organization/${organization_id}/qid/${user.email || user.qid}`, null, {status})
                setUser((prevU)=>{
                    return {...prevU, status}
                })
                doRefresh(prevRefresh=>prevRefresh+1);
                modal.current.alert(true, 'success', 0, t("@Your profile is updated.@"));
            } catch (err) {
                console.log(err);
                modal.current.alert(true, 'error', err.response.data.code, err.response.data.message);
            }

        }
    }

    const resendInvit = async () => {
        try {
            await OrganizationAPI.post(`organization_invitation/organization/${organization_id}/resend`,
            null, {qids: [user.email || user.qid]})
            modal.current.alert(true, 'success', 0, `${t("@An email has been sent to@")} ${user.email || user.qid}`);
        } catch (err) {
            console.log(err);
            modal.current.alert(true, 'error', err.response.data.code, err.response.data.message);
        }
    }


    useEffect(()=>{
        setUser(user_data);
    }, [user_data])

    useEffect(()=>{
        modal.current?.setShowSubmit(false);
    }, [])

    useEffect(()=>{
        if (!openModal) {
            modal.current.alert(false, '', 0, '')
        }
    }, [openModal]);

    return (
        <ModalComponent isOpen={openModal} onClose={closeModal} onSubmit={onSubmit} title={t('@Edit Administator Information@')} modalSize={'none'} okText={''} ref={modal}>
            <div className="admin-basic-info">
                <div className="account-img">
                    <div className={`avatar ${user.avatars?.medium ? '' : 'no-avatar'}`}>
                        {user.avatars?.medium && <img src={`${url.get_acc_url()}/avatar/${user.id}/m`} alt={''} />}
                        {!user.avatars && <span>
                            {user.display_name?.[0] || user.email?.[0] || user.qid?.[0]}
                        </span>}
                    </div>
                </div>
                <div className="account-info">
                    <div className="name">{user.display_name}</div>
                    <div className="mail">{user.email || user.qid}</div>
                </div>
            </div>
            <div className="dialog-content">
                <form className="admin-info-form">
                    <div className="form-row">
                        <div className="form-label"><label>{t('@Join organization since@')}</label></div>
                        <div className="form-field">
                            <div className="field-wrapper">{useTimezone(user.created_at, false)}</div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-label"><label>{t('@Description@')}</label></div>
                        <div className="form-field">
                            <input type="text"
                                   placeholder={t('@APP-SETTINGS_GENERAL_MAX50@')}
                                   value={user.description}
                                   onKeyPress={async (e)=> await updateDescription(e)}
                                   onChange={(e)=>{setUser((prevU)=>{return {...prevU, description: e.target.value}})}}/>
                        </div>
                    </div>

                    { user.status && (user.status === 'active' || user.status === 'suspend') &&
                        <div className="form-row">
                            <div className="form-label"><label>{t('@Activate@')}</label></div>
                            <div className="form-field display-flex flex-direction-column">
                                <div className="switch-wrapper">
                                    <div className="comp-switch">
                                        {/*// <!-- 開關切換，使用 class: switch-on / switch-off -->*/}
                                        <div className={`q-switch ${user.status==='active' ? 'switch-on' : 'switch-off'}`}
                                             onClick={async ()=> await selectStatus(user.status==='active' ? 'suspend' : 'active')}/>
                                    </div>
                                </div>

                                <div className="flex-1 !pt-8 color-gray">{t('@Since@')}{last_update}</div>
                            </div>
                        </div>
                    }

                    { (!user.status || user.status === 'qid_not_activated' || user.status === 'pending') &&
                        <div className="form-row">
                            <div className="form-label"><label>{t('@Account status@')}</label></div>
                            <div className="form-field">
                                <div className="field-wrapper">
                                    <span className="txt !mr-4">{!user.status || user.status === 'qid_not_activated' ? t('@QID not activated@') : t('@Invited@')}</span>
                                </div>
                                <button type="button"
                                        className="q-btn q-btn-default q-btn-small min-w-auto !mt-4 !mb-10"
                                        onClick={async ()=> await resendInvit()}>{t('@Resend Invitation@')}
                                </button>
                            </div>
                        </div>
                    }

                    <div className="form-row">
                        <div className="form-label"><label>{t('@Group@')}</label></div>
                        <div className="form-field">
                            {user?.groups?.length > 0 &&
                                <div className="tag-group">
                                    {
                                        user.groups?.map((g, index) => (
                                            <span className="tag" key={`g-${index}`}>
                                                <span className="tag-label">{g.name}</span>
                                                <span className="close" onClick={async () => await removeGroup(g)}/>
                                            </span>)
                                        )
                                    }
                                </div>
                            }
                            {user?.groups?.length <= 0 &&
                                <div className="field-wrapper">
                                    <span className="txt !mr-4">{t('@Not a member of any group.@')}</span>
                                </div>
                            }
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-label"><label>{t('@Administrator in Site@')}</label></div>
                        <div className="form-field">
                            {sites && sites?.length > 0 &&
                                <div className="tag-group">
                                    {sites && sites.map(site=>(
                                        <span className="tag" key={site.organization_site_id}>
                                            <span className="tag-label">{site.name}</span>
                                            <span className="close" onClick={()=>removeAdmin(site.organization_site_id)}/>
                                        </span>
                                    ))}
                                    {/*<span className="tag"><span className="tag-label">SiteName 1</span><span*/}
                                    {/*    className="close"></span></span>*/}
                                    {/*<span className="tag"><span className="tag-label">SiteName 2</span><span*/}
                                    {/*    className="close"></span></span>*/}
                                </div>
                            }
                            {
                                sites && sites?.length <= 0 &&
                                <div className="field-wrapper">
                                    <span className="txt !mr-4">{t('@No site management permissions assigned.@')}</span>
                                </div>
                            }
                        </div>
                    </div>

                    {((user.status && user?.status !== 'pending') && is_owner) &&
                        <div className="form-row">
                            <div className="form-label"><label>{t('@Owner@')}</label></div>
                            <div className="form-field">
                                <div className="switch-wrapper">
                                    <div className="comp-switch">
                                        {/*// <!-- 開關切換，使用 class: switch-on / switch-off -->*/}
                                        <div className={`q-switch ${isOwner ? 'switch-on' : 'switch-off'}`}
                                             onClick={toggleOwner}/>
                                    </div>
                                </div>
                                {/*<div className="text-error">error message</div>*/}
                            </div>
                        </div>
                    }

                    { ((user.status && user?.status !== 'pending' && !isOwner) && is_owner) &&
                        <div className="form-row">
                            <div className="form-label"><label>{t('@Invite Administrator@')}</label></div>
                            <div className="form-field">
                                <div className="switch-wrapper">
                                    <div className="comp-switch">
                                        {/*// <!-- 開關切換，使用 class: switch-on / switch-off -->*/}
                                        <div className={`q-switch ${canInvite ? 'switch-on' : 'switch-off'}`}
                                             onClick={async () => await toggleInviteAdmin() }/>
                                    </div>
                                </div>
                                {/*<div className="text-error">error message</div>*/}
                            </div>
                        </div>
                    }
                </form>
            </div>
        </ModalComponent>
    );
};

export default AccountListEditAdminModalComponent;